import { lazy } from 'react';
import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';
import RootLayout from '@components/layout/RootLayout';
import Error from '@pages/error/error';
import { v4 as uuidv4 } from 'uuid';
import { getUserFromJWT, StorageUtility, assessmentLegacyRedirectLoader } from '@utils';
import { RID, RL, SK } from '@constants';
import { useProfileSelectionActions } from './profile-selection-store';
import { updateStore } from './store';

// Lazy route surfaces — each becomes its own code-split chunk, downloaded only when its
// route matches. Import the concrete files (NOT the `@pages`/`@components/layout` barrels),
// otherwise a barrel import would pull every page into a single chunk and defeat splitting.
// RootLayout (the shell hosting the Suspense boundary) and Error stay statically imported.
const DynamicCenter = lazy(() => import('@pages/dynamic-center/dynamic-center'));
const CookieCenter = lazy(() => import('@pages/cookie-center/cookie-center'));
const CookieNotice = lazy(() => import('@pages/cookie-center/notice/cookie-notice'));
const Dashboard = lazy(() => import('@components/layout/dasboard/dashboard'));
const Requests = lazy(() => import('@pages/requests/requests'));
const ViewRequests = lazy(() => import('@pages/requests/view-requests'));
const ViewRequest = lazy(() => import('@pages/requests/view-request'));
const ConsentPreferences = lazy(() => import('@pages/consent-preferences/consent-preferences'));
const ViewConsentPreferences = lazy(() => import('@pages/consent-preferences/view-consent-preferences'));
const ViewPurposeDetails = lazy(() => import('@pages/consent-preferences/view-purpose-details'));
const NotificationSettings = lazy(() => import('@pages/notification-settings/notification-settings'));
const ViewNotificationSettings = lazy(() => import('@pages/notification-settings/view-notification-settings'));
const Account = lazy(() => import('@pages/account/account'));
const ProfileSelection = lazy(() => import('@pages/select-profile/profile-selection'));
const Authenticate = lazy(() => import('@pages/authenticate/authenticate'));
const AssessmentResponderForm = lazy(() => import('@pages/assessment-responder-form/assessment-responder-form'));

const getCenterTypeBasisUrlType = (type) => {
  switch (type) {
    case 'consentCollectUrl': return 'consentCenterUrl';
    case 'RQC': return 'requestCenterUrl';
    case 'PFC': return 'preferenceCenterUrl';
    case 'PVC': return 'privacyCenterUrl';
    default: return '';
  }
};

const protectedLoader = async () => {
  const token = StorageUtility.getItem(SK.TOKEN);
  const { setUser } = useProfileSelectionActions();
  if (!token) {
    // ⬇️ This is added because there is a delay in adding the token to sessionStorage, after auth redirection.
    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
    throw redirect(`/${RL.AUTHENTICATE}`);
  }
  const user = getUserFromJWT(token);
  if (user) {
    setUser(user);
  }
  const redirectPath = StorageUtility.getItem(SK.LAST_PATHNAME);
  if (redirectPath) {
    StorageUtility.removeItem(SK.LAST_PATHNAME);
    throw redirect(redirectPath);
  }

  return null;
};

const loader = ({ request }) => {
  const url = new URL(request.url);
  const isIosSdk = url.searchParams.get('device') === 'ios';
  const type = url.searchParams.get('type') || url.searchParams.get('urlType');
  const locale = url.searchParams.get('locale');
  const redirectUrl = url.searchParams.get('redirectUrl') || '';
  const centerType = getCenterTypeBasisUrlType(type);

  updateStore({
    [centerType]: url.href,
    nonce: uuidv4(),
    isIosSdk,
    isAndroidSdk: window.ConsentManagerSDK,
    centerType,
    redirectUrl,
    ...(locale && { locale }),
  });

  return { centerType };
};

const appRoutes = [
  {
    id: 'root',
    path: '/',
    element: <RootLayout />,
    errorElement: <Error />, // needs to be a modal
    children: [
      {
        id: 'collect',
        path: 'collect',
        loader,
        element: <DynamicCenter />,
      },
      {
        id: 'dynamic-center',
        path: 'privacy-center',
        loader,
        element: <DynamicCenter />,
      },
      {
        id: 'cookie-center',
        path: 'cookie-center',
        loader,
        element: <CookieCenter />,
        children: [
          {
            index: true,
            element: <CookieNotice />,
          },
        ],
      },
      {
        id: RID.DASHBOARD,
        path: RL.DASHBOARD,
        loader: protectedLoader,
        element: <Dashboard />,
        children: [
          {
            index: true,
            element: <Navigate to={`${RL.CONSENT_PREFERENCES}`} />,
          },
          {
            id: RID.REQUESTS,
            path: RL.REQUESTS,
            element: <Requests />,

            children: [
              {
                index: true,
                element: <ViewRequests />,
              },
              {
                id: RID.VIEW_REQUEST,
                path: `${RL.ID}`,
                element: <ViewRequest />,
              },
            ],
          },
          {
            id: RID.CONSENT_PREFERENCES,
            path: RL.CONSENT_PREFERENCES,
            element: <ConsentPreferences />,
            children: [
              {
                index: true,
                element: <ViewConsentPreferences />,
              },
              {
                id: RID.VIEW_CONSENT_PREFERENCE,
                path: `${RL.ID}`,
                element: <ViewConsentPreferences />,
              },
              {
                id: RID.VIEW_PURPOSE_DETAILS,
                path: `${RL.PURPOSES}`,
                element: <ViewPurposeDetails />,
              },
            ],
          },
          {
            id: RID.NOTIFICATION_SETTINGS,
            path: RL.NOTIFICATION_SETTINGS,
            element: <NotificationSettings />,
            children: [
              {
                index: true,
                element: <ViewNotificationSettings />,
              },
              {
                id: RID.VIEW_NOTIFICATION_SETTINGS,
                path: `${RL.ID}`,
                element: <ViewNotificationSettings />,
              },
            ],
          },
          {
            id: RID.ACCOUNT,
            path: RL.ACCOUNT,
            element: <Account />,
          },
        ],
      },
      {
        id: RID.SELECT_PROFILE,
        path: RL.SELECT_PROFILE,
        element: <ProfileSelection />,
      },
      {
        id: RID.AUTHENTICATE,
        path: RL.AUTHENTICATE,
        element: <Authenticate />,
      },
      {
        id: RID.ASSESSMENT_RESPONDER_FORM,
        path: RL.ASSESSMENT_RESPONDER_FORM,
        loader,
        element: <AssessmentResponderForm />,
      },
      {
        id: RID.ASSESSMENT_RESPONDER_FORM_LEGACY,
        path: RL.ASSESSMENT_RESPONDER_FORM_LEGACY,
        loader: assessmentLegacyRedirectLoader,
      },
      {
        // Public: opened from the feedback link sent when a request closes.
        id: RID.FEEDBACK,
        path: RL.FEEDBACK,
        loader,
        // Lazy so the feedback page ships in its own chunk, not the main bundle.
        lazy: async () => {
          const { Feedback } = await import('@pages/feedback');
          return { Component: Feedback };
        },
      },
    ],
  },
];

export const appRouter = createBrowserRouter(appRoutes);
